import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
} from '@tanstack/react-table'
import { useParams } from 'react-router-dom'
import { ColumnDefinitionCustomerTransaction } from '../ColumnDefinitionCustomerTransaction'
import { TransactionContext } from '../../../../../../app/contexts/TransactionContext'
import { Transaction } from '@/app/entities/Transaction'
import { useAtom } from 'jotai'
import { selectedIdfromTableRowTransaction } from '@/app/store'

export const useCustomerTransactionTableController = () => {
  const { id } = useParams()
  const [hasRun, setHasRun] = useState(false)

  const { transaction, setSkip, setFilterParams } =
    useContext(TransactionContext)
  const { data, isFetching, isLoading } = useMemo(
    () => transaction || {},
    [transaction],
  )

  const [selectedItem, setSelectedItem] = useState<Transaction | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  const { columnsCustomerTransactions } = ColumnDefinitionCustomerTransaction()
  const [, setSelectedId] = useAtom(selectedIdfromTableRowTransaction)

  const table = useReactTable({
    data: data?.transactions || [],
    columns: columnsCustomerTransactions,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
  })

  const handleRowClick = (item: Transaction) => {
    setSelectedItem(item)
    setSelectedId(item._id)
  }

  const handleSheetState = () => {
    setSheetOpen(!sheetOpen)
  }

  const handleCustomerTransactionsById = useCallback(
    (id: string) => {
      setFilterParams({
        customerIds: id,
      })
    },
    [setFilterParams],
  )

  useEffect(() => {
    if (!hasRun) {
      handleCustomerTransactionsById(id ?? '')
      setHasRun(true)
    }
  }, [handleCustomerTransactionsById, hasRun, id])

  const getPageIndex = () => {
    if (!data) return 0
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1
  }

  const getPageCount = () => {
    if (!data) return 0
    return Math.ceil(data.pagination.total / data.pagination.limit)
  }

  const handleNextPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit + 1
    if (getPageCount() === getPageIndex()) return
    const skipValue = newPage * data.pagination.limit
    setSkip(skipValue)
  }

  const handlePreviousPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit - 1
    if (newPage < 0) return
    const skipValue = newPage * data.pagination.limit
    setSkip(skipValue)
  }

  return {
    table,
    isFetching,
    isLoading,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    selectedItem,
    sheetOpen,
    setSheetOpen,
    handleRowClick,
    handleSheetState,
  }
}
